<template>
  <BaseModal :modalTitle="$t('W: Opret kunde')">
    <div class="flex flex-col gap-4">
      <UiPillNav
        :navItems="pillItems"
        @nav-item-click="onPillClick" />

      <CreateCustomerAccountForm
        v-if="activeType === 'business'"
        :onSuccess="onSuccess" />
      <CreateCustomerUserPrivateForm
        v-if="activeType === 'private'"
        :onSuccess="onSuccess" />
      <CreateInsuranceCustomerAccountForm v-if="activeType === 'insurance'" />
    </div>
  </BaseModal>
</template>

<script lang="ts">
import type { ITabNavOption } from '@team-milva/lab-components'
import type { PropType } from 'vue'
import { useModalStore } from '@/stores/modalStore'
import {
  confirmLeaveUnsaved,
  isModalDirty,
} from '@/composables/useUnsavedChangesGuard'

export type TCustomerCreateType = 'business' | 'private' | 'insurance'

export default {
  name: 'CreateCustomerModal',

  props: {
    defaultCustomerType: {
      type: String as PropType<TCustomerCreateType>,
      required: false,
      default: 'business',
    },
    onSuccess: {
      type: Function as PropType<(id: string) => void>,
      required: false,
      default: undefined,
    },
  },

  data() {
    return {
      activeType: this.defaultCustomerType as TCustomerCreateType,
      modalID: '',
    }
  },

  computed: {
    pillItems(): ITabNavOption[] {
      return [
        {
          id: 'business',
          title: this.$t('area.customer-account.tab.business'),
          isActive: this.activeType === 'business',
        },
        {
          id: 'private',
          title: this.$t('area.customer-account.tab.private'),
          isActive: this.activeType === 'private',
        },
        {
          id: 'insurance',
          title: this.$t('W: Forsikringskunde'),
          isActive: this.activeType === 'insurance',
        },
      ]
    },
  },

  methods: {
    // Swapping the pill unmounts the active child form and discards its draft,
    // but it is not a route change and not a modal close — so none of the
    // guard's three integration points sees it. It is a fourth departure route
    // and has to ask here, or a typed customer is lost silently.
    //
    // Only one child form is mounted at a time and each registers under this
    // modal's own entry id, so isModalDirty answers for the outgoing form.
    async onPillClick(id: string): Promise<void> {
      const nextType = id as TCustomerCreateType
      if (nextType === this.activeType) return

      if (this.modalID && isModalDirty(this.modalID)) {
        const shouldLeave = await confirmLeaveUnsaved()
        // Staying keeps both the draft and the current pill: the child form is
        // never unmounted, so nothing has to be restored.
        if (!shouldLeave) return
      }

      this.activeType = nextType
    },
  },

  created() {
    // Captured once, because confirmLeaveUnsaved() pushes onto the same stack
    // and would make top?.id the confirmation's own id by the time we compare.
    this.modalID = useModalStore().top?.id ?? ''
  },
}
</script>
